import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { ENTITY_TYPE } from '../core/const/entity-type';
import { EntityOption } from '../entity-options-menu/entity-option';
import { FilterMetadata } from 'primeng/api';
import { PARAM_MATCH_MODE } from '../core/grid/param-match-mode';
import { LastEntityItemChecked } from '../entity-list/base-entity-item/last-entity-item-checked';
import { DIALOG_VISIBILITY } from '../core/const/dialog-visibility';
import { SAVE_OPTION } from './save-option';
import { KeyId } from './key-id';
import { ToggledTable } from './toggled-table';
import { EffectiveRole } from './effective-role';
import { ENTITY_ITEM_ID_KEYS_MAP } from '../core/const/entity-item-id-keys-map';

@Injectable({
  providedIn: 'root'
})
export class ServiceAccessDataService {

  private _sharedEntitySelections: {
    [entityType: string]: {
      [idKey: string]: string[]
    }
  } = {
      [ENTITY_TYPE.user]: { connectUserId: [] },
      [ENTITY_TYPE.supportUser]: { connectUserId: [] },
      [ENTITY_TYPE.userGroup]: { userGroupInvariantKey: [] },
      [ENTITY_TYPE.company]: { tenantId: [] },
      [ENTITY_TYPE.clientCompany]: { tenantId: [] },
      [ENTITY_TYPE.companyGroup]: { companyGroupInvariantKey: [] },
      [ENTITY_TYPE.customer]: { tenantId: [] }
    };

  private _roleSelectionsByContext: {
    [contextKey: string]: {
      [idKey: string]: any[]
    }
  } = {};

  private _pendingIds: string[] = [];

  private _lastEntityItemChecked: LastEntityItemChecked = {
    idKey: '',
    id: ''
  }

  private _connectUserIdsBelongToACustomer: string[] = [];

  private _subjectTypeSelected: string = ENTITY_TYPE.user;
  private _contextTypeSelected: string = ENTITY_TYPE.company;
  private _assignmentTypeSelected: string = ENTITY_TYPE.role;

  private _entityOptionTypeSelected: string = ENTITY_TYPE.none;

  private _currentEntityOption: EntityOption = new EntityOption();
  private _oldEntityOption: EntityOption = new EntityOption();

  private _lastEfectiveRolesChanged: EffectiveRole[] = [];

  private _subjectEntityOption = new Subject<EntityOption>();
  public subjectEntityOption$: Observable<EntityOption> = this._subjectEntityOption.asObservable();

  private _contextEntityOption = new Subject<EntityOption>();
  public contextEntityOption$: Observable<EntityOption> = this._contextEntityOption.asObservable();

  private _assignmentEntityOption = new Subject<EntityOption>();
  public assignmentEntityOption$: Observable<EntityOption> = this._assignmentEntityOption.asObservable();

  private _setFocusOn = new Subject<string>();
  public setFocusOn$: Observable<string> = this._setFocusOn.asObservable();

  private _goToRootContext = new Subject<boolean>();
  public goToRootContext$: Observable<boolean> = this._goToRootContext.asObservable();

  private _change = new Subject<{ [key: string]: FilterMetadata }>();
  public change$: Observable<{ [key: string]: FilterMetadata }> = this._change.asObservable();

  private _changeOnCreate = new Subject<{ [key: string]: FilterMetadata }>();
  public changeOnCreate$: Observable<{ [key: string]: FilterMetadata }> = this._changeOnCreate.asObservable();

  private _refresh = new Subject<{ [key: string]: FilterMetadata }>();
  public refresh$: Observable<{ [key: string]: FilterMetadata }> = this._refresh.asObservable();

  private _refreshOnTerminateSubscriber = new Subject<boolean>();
  public refreshOnTerminateSubscriber$: Observable<boolean> = this._refreshOnTerminateSubscriber.asObservable();

  private _resetCheckboxes = new Subject<boolean>();
  public resetCheckboxes$: Observable<boolean> = this._resetCheckboxes.asObservable();

  private _resetRoleGroupCheckboxes = new Subject<boolean>();
  public resetRoleGroupCheckboxes$: Observable<boolean> = this._resetRoleGroupCheckboxes.asObservable();

  private _save = new Subject<string>();
  public save$: Observable<string> = this._save.asObservable();


  private _limitWarningDialog = new Subject<string>();
  public limitWarningDialog$: Observable<string> = this._limitWarningDialog.asObservable();

  private _removeUserFromUserGroupDialog = new Subject<string>();
  public removeUserFromUserGroupDialog$: Observable<string> = this._removeUserFromUserGroupDialog.asObservable();

  private _activateServiceDialog = new Subject<string>();
  public activateServiceDialog$: Observable<string> = this._activateServiceDialog.asObservable();


  private _resetLastEntityItemChecked = new Subject<LastEntityItemChecked>();
  public resetLastEntityItemChecked$: Observable<LastEntityItemChecked> = this._resetLastEntityItemChecked.asObservable();

  private _adjustTableHeight = new Subject<ToggledTable>();
  public adjustTableHeight$: Observable<ToggledTable> = this._adjustTableHeight.asObservable();

  private _refreshUsersList = new Subject<boolean>();
  public refreshUsersList$: Observable<boolean> = this._refreshUsersList.asObservable();

  private _refreshPendingUsers = new Subject<boolean>();
  public refreshPendingUsers$: Observable<boolean> = this._refreshPendingUsers.asObservable();

  private _refreshUsersWithoutAccess = new Subject<boolean>();
  public refreshUsersWithoutAccess$: Observable<boolean> = this._refreshUsersWithoutAccess.asObservable();

  private _refreshUsersWithoutAccessOnAddSelected = new Subject<boolean>();
  public refreshUsersWithoutAccessOnAddSelected$: Observable<boolean> = this._refreshUsersWithoutAccessOnAddSelected.asObservable();

  setEntityOptionSelected(option: EntityOption): void {
    this._entityOptionTypeSelected = option.type;
    this._currentEntityOption = option;
    this._oldEntityOption = option;
  }

  getEntityOptionTypeSelected(): string {
    return this._entityOptionTypeSelected;
  }

  getCurrentEntityOption(): EntityOption {
    return this._currentEntityOption;
  }

  getOldEntityOption(): EntityOption {
    return this._oldEntityOption;
  }

  getSubjectTypeSelected(): string {
    return this._subjectTypeSelected;
  }

  setSubjectTypeSelected(entityType: string): void {
    this._subjectTypeSelected = entityType;
  }

  getContextTypeSelected(): string {
    return this._contextTypeSelected;
  }

  setContextTypeSelected(entityType: string): void {
    this._contextTypeSelected = entityType;
  }

  getAssignmentTypeSelected(): string {
    return this._assignmentTypeSelected;
  }

  setAssignmentTypeSelected(entityType: string): void {
    this._assignmentTypeSelected = entityType;
  }

  getLastEffectiveRolesChanged(): EffectiveRole[] {
    return this._lastEfectiveRolesChanged;
  }

  setLastEffectiveRolesChanged(effectiveRoles: EffectiveRole[]): void {
    this._lastEfectiveRolesChanged = effectiveRoles;
  }

  setSubjectOption(option: EntityOption): void {
    this._subjectTypeSelected = option.type;
    this._subjectEntityOption.next(option);
  }

  setContextOption(option: EntityOption): void {
    this._contextTypeSelected = option.type;
    this._contextEntityOption.next(option);
  }

  setAssignmentOption(option: EntityOption): void {
    this._assignmentTypeSelected = option.type;
    this._assignmentEntityOption.next(option);
  }

  refreshUsersWithoutAccess(): void {
    this._refreshUsersWithoutAccess.next(true);
  }

  refreshUsersWithoutAccessOnAddSelected(): void {
    this._refreshUsersWithoutAccessOnAddSelected.next(true);
  }

  getPendingIds(): string[] {
    return this._pendingIds;
  }

  addToPendingIds(id: string): void {
    this._pendingIds.push(id);
  }

  removeFromPendingIds(id: string): void {
    this._pendingIds = this._pendingIds.filter(value => { return value !== id; });
  }

  isInPendingIds(id: string): boolean {
    return this._pendingIds.filter(value => { return value === id; }).length > 0;
  }

  removeFromPendingIdsAndRefreshTheUserList(id: string): void {
    this._pendingIds = this._pendingIds.filter(value => { return value !== id; });
    this._refreshPendingUsers.next(true);
  }

  addConnectUserIdToBelongsToACustomerArray(connectUserId: string): void {
    this._connectUserIdsBelongToACustomer.push(connectUserId);
  }

  removeConnectUserIdFromBelongsToACustomerArray(connectUserId: string): void {
    this._connectUserIdsBelongToACustomer = this._connectUserIdsBelongToACustomer.filter(value => value !== connectUserId);
  }

  getConnectUserIdsBelongsToACustomerArray(): string[] {
    return this._connectUserIdsBelongToACustomer;
  }

  isIdInConnectUserIdsBelongsToACustomerArray(connectUserId: string): boolean {
    return this._connectUserIdsBelongToACustomer.filter(value => value === connectUserId).length > 0;
  }

  resetConnectUserIdsBelongsToACustomerArray(): void {
    this._connectUserIdsBelongToACustomer = [];
  }

  refreshUserList(): void {
    this._refreshUsersList.next(true)
  }

  getSelectedItems(idKey: string): any[] {
    const state = this.getCurrentSelectionState();
    return state[idKey] || [];
  }

  getCurrentSelectionState(): { [idKey: string]: any[] } {
    const subject = this._subjectTypeSelected;
    const context = this._contextTypeSelected;
    const roleState = this.getRoleStateForCurrentContext();

    // Merge shared entity selections with role selections for current context
    return {
      ...this._sharedEntitySelections[subject],
      ...this._sharedEntitySelections[context],
      ...roleState
    };
  }

  showLimitWarningDialog(): boolean {
    let has = false;
    let count = 0;
    const obj = this.getCurrentSelectionState();
    const keys = Object.keys(obj);
    for (let i = 0, len = keys.length; i < len; i++) {
      if (obj[keys[i]].length > 0) {
        count += 1;
      }
    }
    if (count >= 3) {
      has = true;
    }
    return has;
  }

  hasThreeSelections(): boolean {
    let selectionCount = 0;
    const state = this.getCurrentSelectionState();
    // Check non-role keys
    if (state['connectUserId'] && state['connectUserId'].length > 0) selectionCount++;
    if (state['tenantId'] && state['tenantId'].length > 0) selectionCount++;
    if (state['userGroupInvariantKey'] && state['userGroupInvariantKey'].length > 0) selectionCount++;
    if (state['companyGroupInvariantKey'] && state['companyGroupInvariantKey'].length > 0) selectionCount++;

    // Check role keys (count as 1 if both have values)
    if (state['roleInvariantKey'] && state['roleInvariantKey'].length > 0) {
      selectionCount++;
    }

    return selectionCount >= 3;
  }

  clearSelectionsFor(type: string, idKey: string): void {
    this._entityOptionTypeSelected = type;

    if (idKey === 'roleInvariantKey') {
      const roleState = this.getRoleStateForCurrentContext();
      roleState[idKey] = [];
    } else {
      const entityType = this.getEntityTypeFromIdKey(idKey);
      if (entityType && this._sharedEntitySelections[entityType]) {
        this._sharedEntitySelections[entityType][idKey] = [];
      }
    }

    const value = this.getFilters();
    this._refresh.next(value);
  }

  save(): void {
    this._save.next(SAVE_OPTION.save);
  }

  refresh(): void {
    const entityType = this._entityOptionTypeSelected;
    const value = this.getFilters();
    if (entityType === ENTITY_TYPE.companyGroup || entityType === ENTITY_TYPE.company ||
      entityType === ENTITY_TYPE.clientCompany || entityType === ENTITY_TYPE.customer) {
      this._resetRoleGroupCheckboxes.next(true);
    }
    this._refresh.next(value);
  }

  goToRootContext(): void {
    this._goToRootContext.next(true);
  }

  setFocusOn(type: string): void {
    this._setFocusOn.next(type);
  }

  hideRemoveUserFromUserGroupDialog(): void {
    this._removeUserFromUserGroupDialog.next(DIALOG_VISIBILITY.hide);
  }

  hideAndRefreshRemoveUserFromUserGroupDialog(): void {
    this._removeUserFromUserGroupDialog.next(DIALOG_VISIBILITY.hideAndRefresh);
  }

  hideActivateServiceDialog(): void {
    this._activateServiceDialog.next(DIALOG_VISIBILITY.hide);
  }

  hideAndRefreshActivateServiceDialog(): void {
    this._activateServiceDialog.next(DIALOG_VISIBILITY.hideAndRefresh);
  }

  hideLimitWarningDialog(): void {
    this._limitWarningDialog.next(DIALOG_VISIBILITY.hide);
  }

  change(): void {
    const value = this.getFilters();
    this._change.next(value);
  }

  add(idKey: string, id: string, item: any): void {
    this.addToAppropriateStore(idKey, item);
    this._lastEntityItemChecked = { id: id, idKey: idKey };
    const value = this.getFilters();
    this._change.next(value);
  }

  addOnCreate(idKey: string, id: string, item: any): void {
    this.addToAppropriateStore(idKey, item);
    this._lastEntityItemChecked = { id: id, idKey: idKey };
    const value = this.getFilters();
    this._changeOnCreate.next(value);
  }

  addOnCreateItems(arr: Array<{ idKey: string; id: string; }>, items: any[]): void {
    for (let i = 0, len = arr.length; i < len; i++) {
      this.addToAppropriateStore(arr[i].idKey, items[i]);
    }
    this._lastEntityItemChecked = { id: arr[arr.length - 1].id, idKey: arr[arr.length - 1].idKey };
    const value = this.getFilters();
    this._changeOnCreate.next(value);
  }

  addWithoutTriggerChange(idKey: string, id: string, item: any): void {
    this.addToAppropriateStore(idKey, item);
    this._lastEntityItemChecked = { id: id, idKey: idKey };
  }

  remove(idKey: string, id: string): void {
    this.removeFromAppropriateStore(idKey, id);
    const value = this.getFilters();
    this._change.next(value);
  }

  removeWithoutTriggerChange(idKey: string, id: string): void {
    this.removeFromAppropriateStore(idKey, id);
  }

  resetAndChangeforMain(): void {
    // Reset shared entity selections
    Object.keys(this._sharedEntitySelections).forEach(entityType => {
      Object.keys(this._sharedEntitySelections[entityType]).forEach(idKey => {
        this._sharedEntitySelections[entityType][idKey] = [];
      });
    });

    // Reset role selections for current context
    const roleState = this.getRoleStateForCurrentContext();
    Object.keys(roleState).forEach(key => {
      roleState[key] = [];
    });

    this._entityOptionTypeSelected = ENTITY_TYPE.none;
    const data = this.getFilters();
    this._change.next(data);
  }

  reset(idKey1?: string, id1?: string, idKey2?: string, id2?: string): void {
    // Reset shared entity selections
    this._sharedEntitySelections = {};
    this._roleSelectionsByContext = {};

    this._entityOptionTypeSelected = ENTITY_TYPE.none;
    this._resetCheckboxes.next(true);

    if (idKey1 !== undefined && id1 !== undefined) {
      this.addToAppropriateStore(idKey1, { [idKey1]: id1 });
      if (idKey2 !== undefined && id2 !== undefined) {
        this.addToAppropriateStore(idKey2, { [idKey2]: id2 });
      }
      const data = this.getFilters();
      this._change.next(data);
    }
  }

  resetOnTerminateSubscriber(): void {
    this.resetSelectedIdsForMain();
    this._entityOptionTypeSelected = ENTITY_TYPE.none;
    this._subjectTypeSelected = ENTITY_TYPE.user;
    this._contextTypeSelected = ENTITY_TYPE.company;
    this._assignmentTypeSelected = ENTITY_TYPE.role;
    this._refreshOnTerminateSubscriber.next(true);
  }

  resetSelectedIdsForMain(): void {
    // Reset all shared entity selections
    Object.keys(this._sharedEntitySelections).forEach(entityType => {
      Object.keys(this._sharedEntitySelections[entityType]).forEach(idKey => {
        this._sharedEntitySelections[entityType][idKey] = [];
      });
    });

    // Reset all role selections
    this._roleSelectionsByContext = {};
  }

  resetLastEntityItemChecked(): void {
    const last = this._lastEntityItemChecked;
    const state = this.getCurrentSelectionState();

    if (state[last.idKey]) {
      const arr = state[last.idKey].filter(value => value === last.id);
      if (arr.length > 0) {
        this.removeFromAppropriateStore(last.idKey, last.id);
        this._resetLastEntityItemChecked.next(last);
        this._lastEntityItemChecked = { id: '', idKey: '' };
        const value = this.getFilters();
        this._change.next(value);
      }
    }
  }

  reloadMainServiceAccess(): void {
    const value = this.getFilters();
    this._change.next(value);
  }

  getFilters(): { [key: string]: FilterMetadata } {
    const state = this.getCurrentSelectionState();
    const subjectKey = this.getKeyName(this._subjectTypeSelected);
    const contextKey = this.getKeyName(this._contextTypeSelected);
    const assignmentKey = this.getKeyName(this._assignmentTypeSelected);

    const allowedKeys = [subjectKey, contextKey, assignmentKey].filter(key => key !== undefined && key !== '');

    const filters: { [key: string]: FilterMetadata } = {};
    const map = this.getKeysWithValue(state);
    const keys = Object.keys(map);

    for (let i = 0, len = keys.length; i < len; i++) {
      const key: string = keys[i];
      if (allowedKeys.includes(key)) {
        // Extract IDs from items array
        const res = map[key].map((item: any) => item[key]).filter((value: any) => value !== undefined);
        filters[key] = { matchMode: PARAM_MATCH_MODE.in, value: `[${res.toString().replace(/,/g, '|')}]` };
      }
    }
    return filters;
  }

  hasFilters(): boolean {
    const state = this.getCurrentSelectionState();
    const map = this.getKeysWithValue(state);
    return Object.keys(map).length > 0;
  }

  adjustHeightForTable(name: string, isExpanded: boolean): void {
    this._adjustTableHeight.next({ name: name, isExpanded: isExpanded });
  }

  private getKeysWithValue(state: { [idKey: string]: any[] }): { [key: string]: any[] } {
    let map: { [key: string]: any[] } = {};
    const keys = Object.keys(state);
    for (let i = 0, len = keys.length; i < len; i++) {
      const arr = state[keys[i]];
      if (arr && arr.length > 0) {
        map[keys[i]] = arr;
      }
    }
    return map;
  }

  private getKeyName(type: string): string {
    return ENTITY_ITEM_ID_KEYS_MAP[`${type.charAt(0).toLowerCase()}${type.slice(1)}`];
  }

  private getContextKey(): string {
    return `${this._subjectTypeSelected}_${this._contextTypeSelected}_${this._assignmentTypeSelected}`;
  }

  private getRoleStateForCurrentContext(): { [idKey: string]: string[] } {
    const contextKey = this.getContextKey();

    if (!this._roleSelectionsByContext[contextKey]) {
      this._roleSelectionsByContext[contextKey] = {
        roleInvariantKey: []
      };
    }

    return this._roleSelectionsByContext[contextKey];
  }

  private addToAppropriateStore(idKey: string, item: any): void {
    if (idKey === 'roleInvariantKey') {
      const roleState = this.getRoleStateForCurrentContext();
      if (!roleState[idKey]) {
        roleState[idKey] = [];
      }
      // Check if item already exists
      const exists = roleState[idKey].some((i: any) => i[idKey] === item[idKey]);
      if (!exists) {
        roleState[idKey].push(item);
      }
    } else {
      // Add to shared entity selections
      const entityType = this.getEntityTypeFromIdKey(idKey);
      if (entityType && this._sharedEntitySelections[entityType]) {
        if (!this._sharedEntitySelections[entityType][idKey]) {
          this._sharedEntitySelections[entityType][idKey] = [];
        }
        // Check if item already exists
        const exists = this._sharedEntitySelections[entityType][idKey].some((i: any) => i[idKey] === item[idKey]);
        if (!exists) {
          this._sharedEntitySelections[entityType][idKey].push(item);
        }
      }
    }
  }


  private removeFromAppropriateStore(idKey: string, id: string): void {
    if (idKey === 'roleInvariantKey') {
      const roleState = this.getRoleStateForCurrentContext();
      if (roleState[idKey]) {
        roleState[idKey] = roleState[idKey].filter((item: any) => item[idKey] !== id);
      }
    } else {
      // Remove from shared entity selections
      const entityType = this.getEntityTypeFromIdKey(idKey);
      if (entityType && this._sharedEntitySelections[entityType] && this._sharedEntitySelections[entityType][idKey]) {
        this._sharedEntitySelections[entityType][idKey] =
          this._sharedEntitySelections[entityType][idKey].filter((item: any) => item[idKey] !== id);
      }
    }
  }

  private getEntityTypeFromIdKey(idKey: string): string | undefined {
    const subject = this._subjectTypeSelected;
    const context = this._contextTypeSelected;

    // Check if idKey belongs to subject entity
    if (this._sharedEntitySelections[subject] && this._sharedEntitySelections[subject].hasOwnProperty(idKey)) {
      return subject;
    }

    // Check if idKey belongs to context entity
    if (this._sharedEntitySelections[context] && this._sharedEntitySelections[context].hasOwnProperty(idKey)) {
      return context;
    }

    return undefined;
  }

}
